import { CloseOutlined, GlobalOutlined, MessageOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import * as React from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getActiveUser } from '../../../../react4xp/parts/admin-header/actions';
import { IUser } from '../../../../react4xp/parts/admin-users/admin-users.type';
import { getSectionsApi } from '../../knowledge-base-common/actions';
import { sendEmail } from '../../main/actions';
import { EmailSource } from '../../main/actions.types';
import { ASSISTANT_REPLY_DELAY } from '../virtual-assistant.constants';
import { delay, fromAssistant } from '../virtual-assistant.utils';
import { ActionTypes, EmailFormData, From, Message, Tabs } from '../vitrual-assistant.type';
import Chat from './chat';
import LanguageSelector from './language-selector';
import OperatorRequestForm from './operator-request-form';
import { localize } from '/react4xp/mixins/helpers';
import Button from '/react4xp/mixins/ui/button/button';
import { PartContext } from '/react4xp/parts/virtual-assistant/virtual-assistant';

interface VirtualAssistantOpenProps {
    close: () => void;
    translations: any;
    language: string;
    token: string;
}

export default function VirtualAssistantOpen({
    close,
    translations,
    language,
    token,
}: Readonly<VirtualAssistantOpenProps>) {
    const [activeTab, setActiveTab] = useState<Tabs>(Tabs.CHAT);
    const [emailForm] = Form.useForm<EmailFormData>();
    const [isValid, setIsValid] = useState<boolean>(false);
    const [messages, setMessages] = useState<Message[]>(() => {
        const messages = [
            fromAssistant(localize('messages.greeting', translations)),
            fromAssistant(localize('messages.kb-link', translations), {
                type: ActionTypes.KNOWLEDGE_BASE,
            }),
            fromAssistant(localize('messages.consultations', translations), {
                type: ActionTypes.CONSULTATIONS,
            }),
            fromAssistant(localize('messages.select-section', translations)),
        ];

        return messages.reverse().map((message, index) => ({
            id: +1 + index,
            ...message,
        }));
    });
    const [isTyping, setIsTyping] = useState<boolean>();
    const latestMessages = useRef<Message[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { serviceUrl } = useContext(PartContext);

    const [activeUser, setActiveUser] = useState<IUser>(null);

    useEffect(() => {
        if (token) {
            getActiveUser()
                .then((res) => res.data)
                .then((res) => {
                    emailForm.setFieldsValue({
                        email: res.email,
                        name: res.fullname,
                    });

                    setActiveUser(res);
                });
        }
    }, []);

    const mainActions = [
        <Button
            icon={<MessageOutlined />}
            onClick={() => setActiveTab(Tabs.CHAT)}
            type="text"
            uppercase={false}
            style={{
                color: '#3457B1',
            }}
        >
            {localize('chat', translations)}
        </Button>,
        <Button
            icon={<GlobalOutlined size={20} />}
            onClick={() => setActiveTab(Tabs.LANGUAGE)}
            type="text"
            uppercase={false}
        >
            {localize('language-selector', translations)}
        </Button>,
    ];

    const emailOperator = useCallback(() => {
        setActiveTab(Tabs.OPERATOR);
    }, [translations, language]);

    const handleSendEmail = useCallback(() => {
        setIsLoading(true);

        const values = emailForm.getFieldsValue();
        const formData = new FormData();

        formData.append('identifier', activeUser && activeUser.login);
        formData.append('content', values.content);
        formData.append('name', values.fullname);
        formData.append('phoneNumber', values.phoneNumber);
        formData.append('email', values.email);
        formData.append('source', EmailSource.VirtualAssistant);

        sendEmail(formData)
            .then(() => {
                addMessage(fromAssistant(localize('messages.message-sent', translations)));
                emailForm.resetFields();
                setActiveTab(Tabs.CHAT);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [emailForm]);

    const handleCancel = useCallback(() => {
        emailForm.resetFields();
        setActiveTab(Tabs.CHAT);
    }, [emailForm]);

    const operatorFormActions = [
        <Button
            onClick={handleSendEmail}
            type="primary"
            disabled={!isValid}
            loading={isLoading}
        >
            {localize('button.send', translations)}
        </Button>,
        <Button
            onClick={handleCancel}
            loading={isLoading}
        >
            {localize('cancel', translations)}
        </Button>,
    ];

    const addMessage = useCallback(async (...messages: Omit<Message, 'id'>[]) => {
        if (messages[0].from === From.ASSISTANT) {
            setIsTyping(true);
            await delay(ASSISTANT_REPLY_DELAY);
            setIsTyping(false);
        }
        setMessages((prev = []) => [
            ...messages.reverse().map((message, index) => ({
                id: prev.length + 1 + index,
                ...message,
            })),
            ...prev,
        ]);
    }, []);

    useEffect(() => {
        latestMessages.current = messages;
    }, [messages]);

    const saveMessageHistory = useCallback(() => {
        if (!latestMessages?.current.length) return;
        sessionStorage.setItem('virtualAssistantMessages', JSON.stringify(latestMessages.current));
    }, []);

    useEffect(() => {
        // addMessage(
        //     fromAssistant(localize('messages.greeting', translations)),
        //     fromAssistant(localize('messages.kb-link', translations), {
        //         type: ActionTypes.KNOWLEDGE_BASE,
        //     }),
        //     fromAssistant(localize('messages.consultations', translations), {
        //         type: ActionTypes.CONSULTATIONS,
        //     })
        // );

        getSectionsApi(serviceUrl).then((sections) =>
            addMessage(
                ...sections.map((section) =>
                    fromAssistant(section.title, {
                        type: ActionTypes.SELECT_SECTION,
                        payload: section.id,
                    })
                )
            )
        );

        window.addEventListener('beforeunload', saveMessageHistory);

        return () => {
            window.removeEventListener('beforeunload', saveMessageHistory);
            saveMessageHistory();
        };
    }, [saveMessageHistory]);

    return (
        <div className="virtual-assistant">
            <div className="virtual-assistant_header">
                <span style={{ textTransform: 'uppercase' }}>
                    {localize('title', translations)}
                </span>
                <CloseOutlined
                    onClick={close}
                    className="virtual-assistant__close_button"
                />
            </div>
            <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
                {activeTab === Tabs.CHAT ? (
                    <Chat
                        messages={messages}
                        addMessage={addMessage}
                        emailOperator={emailOperator}
                        isTyping={isTyping}
                    />
                ) : activeTab === Tabs.LANGUAGE ? (
                    <LanguageSelector translations={translations} />
                ) : (
                    <OperatorRequestForm
                        emailForm={emailForm}
                        setIsValid={setIsValid}
                        translations={translations}
                    />
                )}
            </div>
            <div className="virtual-assistant_actions">
                {activeTab === Tabs.OPERATOR ? operatorFormActions : mainActions}
            </div>
        </div>
    );
}
